import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import { ProgressSpinner } from "primereact/progressspinner";
import { Utility } from "../../core/Utility";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { getProductInWarehousesReportTemplate } from "../../document-templates/ProductInWarehouseReportTemplate";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { ProductInWarehousesReport } from "../../models/Report";
import { RoleType } from "../../models/Role";
import { ParamSettings } from "../../models/ParamSettings";
import { activeUser } from "../../services/AuthService";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllDocuments } from "../../services/NewVersionService";
import { getAllSettings } from "../../services/SettingsService";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import "./ProductInWarehousesReportPage.scss";

export const ProductInWarehousesReportPage = () => {
  const { t } = useTranslation();

  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31);

  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      const from = new Date(today.getFullYear(), 0, 1);
      const to = today;
      return { from, to };
    } else {
      return { from: yearStart, to: yearEnd };
    }
  };
  const [dateFrom, setDateFrom] = useState<Date>(
    getDefaultRangeForBusinessYear().from
  );
  const [dateTo, setDateTo] = useState<Date>(
    getDefaultRangeForBusinessYear().to
  );

  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [productCategoriesApi, setProductCategoriesApi] = useState<
    ProductCategory[]
  >([]);
  const [settingsApi, setSettingsApi] = useState<ParamSettings[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product>(
    {} as Product
  );
  const [selectedProductCategory, setSelectedProductCategory] =
    useState<ProductCategory>({} as ProductCategory);
  const [productInWarehousesData, setProductInWarehousesData] = useState<
    ProductInWarehousesReport[]
  >([]);
  const [totalNeto, setTotalNeto] = useState<number>(0);
  const [totalSrps, setTotalSrps] = useState<number>(0);
  const [changeState, setChangeState] = useState<boolean>(false);
  const [processing, setProcessing] = useState(false);

  const [pdfData, setPdfData] = useState<any>(
    getProductInWarehousesReportTemplate(
      productInWarehousesData,
      selectedProduct.name ?? selectedProductCategory.name,
      totalNeto,
      totalSrps
    )
  );
  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  const [graph1Data, setGraph1Data] = useState<any>({
    labels: [],
    datasets: [
      {
        label: t("Stanje neto"),
        backgroundColor: "#198754",
        data: [],
      },
      {
        label: t("Stanje Srps"),
        backgroundColor: "#A9E1C5",
        data: [],
      },
    ],
  });

  const [graph2Data, setGraph2Data] = useState<any>({
    labels: [],
    datasets: [
      {
        label: t("Ponderisana vlaga"),
        backgroundColor: "#495057",
        data: [],
      },
      {
        label: t("Ponderisane primese"),
        backgroundColor: "#6C757D",
        data: [],
      },
    ],
  });

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [pResp, pcResp]: [AxiosResponse, AxiosResponse] = await Promise.all([
      getAllProducts(),
      getAllProductCategories(),
    ]);
    [pResp, pcResp].forEach(ensureSuccess);

    setProductsApi(pResp?.data?.result ?? []);
    setProductCategoriesApi(pcResp?.data?.result ?? []);
  };

  const refreshSettings = async () => {
    const sResp = await getAllSettings();
    ensureSuccess(sResp);
    setSettingsApi(sResp?.data?.result ?? []);
  };

  useEffect(() => {
    setProcessing(true);
    Promise.all([refreshSettings(), refreshLookups()])
      .then(() => setChangeState((prev) => !prev))
      .finally(() => setProcessing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // reset states on filter change
    setProductInWarehousesData([]);
    setChangeState((prev) => !prev);
  }, [selectedProduct, selectedProductCategory, dateFrom, dateTo]);

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      await formatProductInWarehousesReportData();
      setProcessing(false);
    };
    run();
  }, [changeState]);

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
  }, [businessYear]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getProductInWarehousesReportTemplate(
      productInWarehousesData,
      selectedProduct.name ?? selectedProductCategory.name ?? "Sve",
      totalNeto,
      totalSrps
    );
    setPdfData(newPdf);
  }, [productInWarehousesData]);

  const formatProductInWarehousesReportData = async () => {
    // uvek računamo od nule za izabrani period/proizvod
    let copyOfProductInWarehousesData: ProductInWarehousesReport[] = [];

    // povuci ULaze i IZlaze merkantile preko novog API-ja
    const [inputsResponse, outputsResponse]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(true, true, dateFrom, dateTo), // prijemnice merkantile
        getAllDocuments(true, false, dateFrom, dateTo), // otpremnice merkantile
      ]);

    const inputDocs = inputsResponse.data.result;
    const outputDocs = outputsResponse.data.result;

    // helper za filter po kategoriji/proizvodu (koristi BE polja categoryId/productId)
    const matchesCategoryOrProduct = (doc: any) => {
      if (selectedProductCategory?.id === undefined) return true;

      if (selectedProduct?.id === undefined) {
        // filtriramo po grupi robe
        return (
          selectedProductCategory.id?.toString() === doc.categoryId?.toString()
        );
      }

      // filtriramo po konkretnoj robi
      return selectedProduct.id?.toString() === doc.productId?.toString();
    };

    // zajednička funkcija za sabiranje, sign = +1 (ulaz) / -1 (izlaz)
    const accumulate = (doc: any, sign: 1 | -1) => {
      if (doc?.isDocumentFinished !== true || !matchesCategoryOrProduct(doc)) {
        return;
      }

      const warehouseName = doc.warehouseName as string;
      const netto = (doc.netto ?? 0) * sign;
      const srps = (doc.srps ?? 0) * sign;

      let moisturePercent: number | undefined;
      let impurityPercent: number | undefined;

      if (doc.calculationMethod === true) {
        // svodjenje na srps ili neto metod – koristi vrednosti sa dokumenta
        moisturePercent = doc.moisture;
        impurityPercent = doc.impurity;
      } else {
        // koristi podešavanja iz settings po nazivu robe
        const setting = settingsApi.find(
          (item) => item.mercantile === doc.productName
        );
        moisturePercent = setting?.moisture;
        impurityPercent = setting?.impurity;
      }

      const idx = copyOfProductInWarehousesData.findIndex(
        (item) => item.warehouseName === warehouseName
      );

      const baseNetto = doc.netto ?? 0; // za ponderisanje koristimo apsolutni ulaz/izlaz
      const moistureDelta = (baseNetto * (moisturePercent ?? 0) * sign) / 100;
      const impurityDelta = (baseNetto * (impurityPercent ?? 0) * sign) / 100;

      if (idx >= 0) {
        copyOfProductInWarehousesData[idx].amount += netto;
        copyOfProductInWarehousesData[idx].amountSrps += srps;
        copyOfProductInWarehousesData[idx].moisturePonder += moistureDelta;
        copyOfProductInWarehousesData[idx].impurityPonder += impurityDelta;
      } else {
        copyOfProductInWarehousesData.push({
          id: doc.id,
          warehouseName,
          amount: netto,
          amountSrps: srps,
          moisturePonder: moistureDelta,
          impurityPonder: impurityDelta,
        });
      }
    };

    // ULazi
    for (const mercantileInput of inputDocs) {
      accumulate(mercantileInput, 1);
    }

    // IZlazi
    for (const mercantileOutput of outputDocs) {
      accumulate(mercantileOutput, -1);
    }

    // izračunavanje ponderisanih procenata vlage i nečistoće
    for (const row of copyOfProductInWarehousesData) {
      if (row.amount !== 0) {
        row.moisturePonder = parseFloat(
          ((row.moisturePonder / row.amount) * 100).toFixed(2)
        );
        row.impurityPonder = parseFloat(
          ((row.impurityPonder / row.amount) * 100).toFixed(2)
        );
      } else {
        row.moisturePonder = 0;
        row.impurityPonder = 0;
      }
    }

    // ako je magacioner – vidi samo svoj magacin
    if (activeUser?.role?.name === RoleType.Warehouseman) {
      copyOfProductInWarehousesData = copyOfProductInWarehousesData.filter(
        (item) => item.warehouseName === activeUser?.warehouse?.name
      );
    }

    // ukupne neto i srps vrednosti
    let newTotalNetoValue = 0;
    let newTotalSrpsValue = 0;
    for (const row of copyOfProductInWarehousesData) {
      newTotalNetoValue += row.amount;
      newTotalSrpsValue += row.amountSrps;
    }
    setTotalNeto(newTotalNetoValue);
    setTotalSrps(newTotalSrpsValue);

    // graf 1 – stanja
    const newGraph1Data = cloneDeep(graph1Data);
    newGraph1Data.labels = [];
    newGraph1Data.datasets[0].data = [];
    newGraph1Data.datasets[1].data = [];
    for (const item of copyOfProductInWarehousesData) {
      newGraph1Data.labels.unshift(item.warehouseName);
      newGraph1Data.datasets[0].data.unshift(item.amount);
      newGraph1Data.datasets[1].data.unshift(item.amountSrps);
    }
    setGraph1Data(newGraph1Data);

    // graf 2 – ponderisana vlaga/prime
    const newGraph2Data = cloneDeep(graph2Data);
    newGraph2Data.labels = [];
    newGraph2Data.datasets[0].data = [];
    newGraph2Data.datasets[1].data = [];
    for (const item of copyOfProductInWarehousesData) {
      newGraph2Data.labels.unshift(item.warehouseName);
      newGraph2Data.datasets[0].data.unshift(item.moisturePonder);
      newGraph2Data.datasets[1].data.unshift(item.impurityPonder);
    }
    setGraph2Data(newGraph2Data);

    setProductInWarehousesData(copyOfProductInWarehousesData);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(selectedProduct);
    copyOfNewProduct = productsApi.find((item) => item.id == value) as Product;
    setSelectedProduct(copyOfNewProduct);
  };

  const handleProductCategoryChange = (prop: string, value: any) => {
    setSelectedProduct({} as Product);
    const copyOfNewProductCategory = productCategoriesApi.find(
      (item) => item.id == value
    ) as ProductCategory;
    setSelectedProductCategory(copyOfNewProductCategory);
  };

  const amount = (rowData: ProductInWarehousesReport) => {
    return Utility.addThousandSeparator(rowData.amount);
  };

  const amountSrps = (rowData: ProductInWarehousesReport) => {
    return Utility.addThousandSeparator(rowData.amountSrps);
  };

  const showMoisturePonder = (rowData: ProductInWarehousesReport) => {
    return <span>{rowData.moisturePonder} %</span>;
  };

  const showImpurityPonder = (rowData: ProductInWarehousesReport) => {
    return <span>{rowData.impurityPonder} %</span>;
  };

  const {
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  return (
    <PageLayout>
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-shop-window header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-title">
              {t("Stanje robe po magacinima za")}
            </span>
            <DropdownInput
              id="productCategory"
              value={selectedProductCategory?.id}
              onValueChange={handleProductCategoryChange}
              optionValue="id"
              optionLabel="name"
              options={productCategoriesApi.filter(
                (item) => item.type == ProductType.MercantileGoods
              )}
              filter={false}
              labelText=""
              placeholderText={t("grupu robe")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={processing}
            />
            <span className="header-elements"></span>
            <DropdownInput
              id="product"
              value={selectedProduct?.id}
              onValueChange={handleProductChange}
              optionValue="id"
              optionLabel="name"
              options={productsApi.filter(
                (item) => item.category?.id === selectedProductCategory?.id
              )}
              filter={false}
              labelText=""
              placeholderText={t("Naziv robe")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={selectedProductCategory?.name === undefined}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (downloadPdfDocument.current) {
                  // @ts-ignore
                  downloadPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (printPdfDocument.current) {
                  // @ts-ignore
                  printPdfDocument.current();
                }
              }}
            />
          </div>
        </div>
        <div className="wrapper-data-table">
          {processing ? (
            <ProgressSpinner
              style={{ width: "100px", height: "100px", color: "green" }}
              strokeWidth="4"
              animationDuration=".8s"
            />
          ) : (
            <DataTable
              value={productInWarehousesData}
              showGridlines
              stripedRows
            >
              <Column
                field="warehouseName"
                header={t("Magacin")}
                footer={t("Ukupno")}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={amount}
                header={t("Stanje neto")}
                footer={Utility.addThousandSeparator(totalNeto)}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={amountSrps}
                header={t("Stanje Srps")}
                footer={Utility.addThousandSeparator(totalSrps)}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={showMoisturePonder}
                header={t("Ponderisana vlaga")}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={showImpurityPonder}
                header={t("Ponderisane primese")}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
            </DataTable>
          )}
        </div>
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t("Stanje po magacinima za robu")} ${
              selectedProduct.name ?? selectedProductCategory.name ?? ""
            }_${Utility.formatDate(new Date())}.pdf`}
          />
        </div>
        {productInWarehousesData.length > 0 && (
          <div className="graph-container">
            <div className="graph-item">
              <Chart type="bar" data={graph1Data} />
            </div>
            <div className="graph-item">
              <Chart type="bar" data={graph2Data} />
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
